<template>
    <div>
        <div class="content-widget map">
            <div
                class="content-widget"
                :class="[standalone ? 'alternative-standalone' : 'map']"
                ref="mapHolder"
                @click="antiScroll()"
            />
            <!--
                Écran de chargement de la carte Karto : le moteur met plusieurs secondes
                à s'initialiser et le conteneur reste vide entre-temps. Rien de tel sur
                la carte Google (es/pt/ca), qui s'affiche immédiatement.
            -->
            <div
                v-if="isKarto && isMapLoading"
                class="map-loader"
            >
                <img
                    class="map-loader-logo"
                    src="@/assets/images/bienici-logo-anime.svg"
                    alt=""
                />
            </div>
            <ScrollButton
                v-if="isWheelLocked"
                :toggleModeWheel="toggleModeWheel"
                :options="data"
            />
            <!--
                Barre de navigation héritée de Google Maps (zoom, plein écran, aide) :
                elle est masquée sur la carte Karto, qui fournit déjà ses propres
                contrôles. Sur mobile les deux barres se superposaient, celle de Karto
                étant en bas au centre et la nôtre en bas à droite.
            -->
            <ThreeDController
                v-if="data.gui && data.gui.type === 'map' && !isKarto"
                :data="data"
                :widget="widget"
                :widgetType="'map'"
                :targetElement="targetElement"
            />
        </div>
        <PopupInformation
            :data="popup"
            :update="updateWidget"
            :target="targetElement"
            :popupType="'hotspot'"
        />
    </div>
</template>


<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';

    import PopupInformation from '@/components/plotInformation/PopupInformation';
    import ScrollButton from "@/components/ScrollButton";
    import ThreeDController from "@/components/threeDController/ThreeDController";
    import { isKartoMap } from '@/utils/mapProvider';

    export default {
        name: 'Map',
        components: {
            ThreeDController,
            PopupInformation,
            ScrollButton
        },
        props: [
            'data',
            'link',
            'active',
            'standalone'
        ],
        data () {
            return {
                targetElement: null,
                widget: null,
                popup: {
                    x: 0,
                    y: 0,
                    visible: false
                },
                toggleModeWheel: false,
                isFavoriteActivated: false,
                isMapLoading: true
            };
        },
        computed: {
            ...mapGetters([
                'isReviewUse',
                'language',
                'protocol'
            ]),
            isWheelLocked() {
                return window.self !== window.top;
            },
            isKarto() {
                return isKartoMap(this.language);
            },
            apiUrl() {
                return 'https://' + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            }
        },
        mounted() {
            this.targetElement = this.$refs.mapHolder;
            this.render();
        },
        methods: {
            ...mapActions([
                'setIsLoading'
            ]),
            updateCallback(link) {
                this.updateWidget(link);
            },
            callbacks() {
                let vm = this;
                return {
                    callbacks: {
                        clickCallback(hotspot, x, y) {
                            if (vm.popup.visible === true)
                                vm.closePopup();
                            else
                                vm.popup = Object.assign(hotspot, { x, y });
                        },
                        overCallback(hotspot, x, y) {
                            vm.popup = Object.assign(hotspot, { x, y });
                        },
                        outCallback() {
                            vm.popup = { 'x': 0, 'y': 0 };
                        },
                        widgetReadyCallback(widget){
                            EventBus.$emit('widgetReady',widget);
                            vm.setIsLoading(false);
                            vm.isMapLoading = false;
                        }
                    }
                };
            },
            closePopup() {
                this.popup = {
                    marginLeft: 0,
                    marginRight: 0,
                    x: 0,
                    y: 0,
                    positionClass: ''
                };
            },
            render() {
                this.isMapLoading = true;
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/map-view' + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        payload.type = 'map';
                        payload.gui.type = 'map';
                        if (payload.language)
                            this.$i18n.locale = payload.language;
                        payload = Object.assign(payload, this.callbacks(), { refreshHotspots: this.refreshHotspots });
                        this.widget = new Habiteo.MapWidget(this.$refs.mapHolder, payload);
                        EventBus.$emit('trackWidget', payload.tracking);
                        if (this.standalone) {
                            EventBus.$emit('setSidebarData', payload);
                            EventBus.$emit('updateLoadedWidgets', { id: 'map-view', link: urlLink, active: true });
                        } else 
                            EventBus.$emit('updateSidebar', payload, false);
                    }
                }).catch(error => {
                    this.isMapLoading = false;
                    EventBus.$emit('setErrorState', true, error);
                });
            },
            updateWidget() {
                // Itinéraire retiré : la popup hotspot n'a pas d'action de mise à jour.
            },
            refreshHotspots(category, state) {
                const hotspots = this.data.hotspots;
                category.references.forEach(reference => {
                    hotspots.filter(hotspot => hotspot.id == reference).forEach(hotspot => hotspot.visible = state);
                });
                this.widget.refresh(hotspots);
            },
            antiScroll(){
                // La carte occupe tout le widget : un clic peut arriver avant que le
                // payload soit chargé et que le widget core soit instancié.
                if (!this.widget)
                    return;
                this.widget.unlockMouseWheel();
                this.toggleModeWheel = !this.toggleModeWheel;
            }
        },
        watch: {
            active(val) {
                if (val)
                    this.render();
            }
        }
	};
</script>

<style lang="scss">
// Écran de chargement de la carte Karto (logo Bien'ici animé).
.map-loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    z-index: 12;

    &-logo {
        width: 60%;
        max-width: 280px;
    }
}

// Disable ALL transitions on map elements to prevent drag resistance
.content-widget.map,
.content-widget.map *,
.content-widget.map .gm-style,
.content-widget.map .gm-style * {
    touch-action: auto !important;
    user-select: auto !important;
    transition: none !important;
    -webkit-transition: none !important;
    -moz-transition: none !important;
    -o-transition: none !important;
    -ms-transition: none !important;
}
</style>



// WEBPACK FOOTER //
// src/views/MapWidget.vue